'use client';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem, Theme } from '@mui/material';
import { MouseEvent, ReactElement, useId, useState } from 'react';

export type RequestCardMenuItem = {
  /** Identifies the item. Used for the item's `data-action`. */
  key: string;
  label: string;
  onClick: () => void;
};

export type RequestCardMenuProps = {
  /** The name of the button, for a screen reader. */
  label: string;
  items: RequestCardMenuItem[];
};

const menuItemSx = (theme: Theme) => ({
  paddingBlock: theme.semantic.spacing.xs,
  paddingInline: theme.semantic.spacing.lg,
  fontSize: theme.semantic.fontSize.sm,
  fontWeight: theme.semantic.fontWeight.semibold,
  // The board draws the menu's words in capitals, as it draws the buttons'.
  textTransform: 'uppercase' as const,
});

/**
 * The round menu button at the top right of a request card, and the menu it opens. MUI's Menu
 * closes on Escape and on a tap outside, and puts focus back on the button.
 */
export function RequestCardMenu({ label, items }: Readonly<RequestCardMenuProps>): ReactElement {
  const buttonId = useId();
  const menuId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = anchor !== null;

  // Safari does not focus a button on a click. The menu puts focus back on the element that was
  // focused when it opened, so the button is focused first.
  const openFrom = (button: HTMLElement) => {
    button.focus();
    setAnchor(button);
  };

  const choose = (item: RequestCardMenuItem) => {
    setAnchor(null);
    item.onClick();
  };

  return (
    <>
      <IconButton
        id={buttonId}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-action="request-card-menu"
        onClick={(event: MouseEvent<HTMLElement>) => openFrom(event.currentTarget)}
        sx={(theme: Theme) => ({
          width: theme.requestCard.menuButtonSize,
          height: theme.requestCard.menuButtonSize,
          flex: 'none',
        })}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        MenuListProps={{ 'aria-labelledby': buttonId }}
        // The board draws the menu beside the button, at its left, level with its top.
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.key}
            data-action={`request-card-menu-${item.key}`}
            sx={menuItemSx}
            onClick={() => choose(item)}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
